import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { useResultHandler } from "../../../hooks/useResultHandler";
import { updateNews as updateNewsRequest } from "../api/news.api";
import type { NewsSubmitRequest } from "../types/news.types";

interface UseUpdateNewsReturn {
  loading: boolean;
  error: string | null;
  success: boolean;
  updateNews: (id: string, data: NewsSubmitRequest) => Promise<void>;
  reset: () => void;
}

export const useUpdateNews = (): UseUpdateNewsReturn => {
  const { t } = useTranslation();
  const { handleResult } = useResultHandler();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const updateNews = useCallback(
    async (id: string, data: NewsSubmitRequest) => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(false);

        const response = await updateNewsRequest(id, data);
        const isSuccess = handleResult(response, {
          successMessage: t("news.news_updated_successfully"),
          showErrorToast: false,
        });

        if (isSuccess) {
          setSuccess(true);
          return;
        }

        setError(response.user_message || response.message || "Failed to update news");
      } catch (caughtError: unknown) {
        setError(getApiErrorMessage(caughtError, "Failed to update news"));
      } finally {
        setLoading(false);
      }
    },
    [handleResult, t],
  );

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setSuccess(false);
  }, []);

  return { loading, error, success, updateNews, reset };
};
